// SectionUnknownPage — 部門不明 line 一覧 + 18 freee 部門 dropdown 確定 + 辞書学習
//   G-step3d (2026-05-21):
//     - proposal details に section_id=null line を全 proposal scan で list 化
//     - 1 click で 18 freee 部門 を dropdown 選択 → Save
//     - Save click → section-assign で proposed_deal_json 更新 + section-aliases/add で 辞書学習
//
//   絶対遵守:
//     R3: partner 自動作成 禁止 (= 本 page は section のみ操作、 partner には触らない)
//     R9: recordProcessingFlag 削除/コメント禁止 (= UI のみで worker 内部 logic は変更しない)
//     絵文字禁止 (= react-icons/md の MdHelp / MdSave / MdRefresh のみ)

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  PageTitle,
  CardBase,
  HStack,
  VStack,
  Button,
  Text,
  Note,
  Breadcrumbs,
  MessageBlock,
  InlineSpinner,
  NoDataIllust,
} from '@freee_jp/vibes';
import { MdHelp, MdSave, MdRefresh } from 'react-icons/md';

const API_BASE = 'https://invoice-review.system-0f7.workers.dev';

interface SectionRow {
  section_id: number;
  name: string;
  long_name: string | null;
}

interface UnknownLine {
  proposal_id: number;
  ref_number: string;
  arica_rowid: number | null;
  line_index: number;
  description: string;
  amount: number | null;
  suggested_section_name: string | null;
  partner_name: string | null;
  status: string;
  proposed_at: string | null;
}

const fmtAmount = (n: number | null | undefined) =>
  n == null ? '—' : `¥${n.toLocaleString('ja-JP')}`;

const STYLES = {
  page: {
    background: 'linear-gradient(180deg, #f8f7f5 0%, #f3f1ef 100%)',
    minHeight: '100vh',
    paddingBottom: '3rem',
  } as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.8125rem',
  } as React.CSSProperties,
  thCell: {
    padding: '11px 14px',
    fontSize: '0.6875rem',
    fontWeight: 700,
    color: '#6b6560',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    whiteSpace: 'nowrap' as const,
    borderBottom: '1px solid #e5e4e2',
    background: 'linear-gradient(180deg, #fafaf8 0%, #f5f3f2 100%)',
    textAlign: 'left' as const,
  } as React.CSSProperties,
  tdCell: {
    padding: '12px 14px',
    verticalAlign: 'middle' as const,
    borderBottom: '1px solid #f0efed',
  } as React.CSSProperties,
  badge: (color: string) => ({
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.6875rem',
    fontWeight: 600,
    background: `${color}22`,
    color,
    border: `1px solid ${color}44`,
  } as React.CSSProperties),
  select: {
    padding: '6px 10px',
    border: '1px solid #d4d2d0',
    borderRadius: '4px',
    fontSize: '0.8125rem',
    background: '#fff',
    minWidth: '180px',
  } as React.CSSProperties,
};

export const SectionUnknownPage: React.FC = () => {
  const [sections, setSections] = useState<SectionRow[]>([]);
  const [items, setItems] = useState<UnknownLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // row-key (= proposal_id:line_index) → selected section_id
  const [selected, setSelected] = useState<Record<string, number>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sRes, lRes] = await Promise.all([
        fetch(`${API_BASE}/api/freee-auto/sections`),
        fetch(`${API_BASE}/api/admin/section-unknown-list?limit=500`),
      ]);
      if (!sRes.ok) throw new Error(`sections: HTTP ${sRes.status}`);
      if (!lRes.ok) throw new Error(`unknown-list: HTTP ${lRes.status}`);
      const sData = await sRes.json();
      const lData = await lRes.json();
      setSections(sData.rows || []);
      setItems(lData.items || []);
    } catch (e: any) {
      setError(e.message || 'load failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const doSave = useCallback(async (item: UnknownLine) => {
    const key = `${item.proposal_id}:${item.line_index}`;
    const sectionId = selected[key];
    if (!sectionId) {
      setSaveError(`row ${key}: 部門を選択してください`);
      return;
    }
    const sec = sections.find((s) => s.section_id === sectionId);
    const sectionName = sec ? (sec.long_name || sec.name) : null;
    setSavingKey(key);
    setSaveError(null);
    setSavedKey(null);
    try {
      // (1) proposed_deal_json.details[line_index].section_id 更新
      const r1 = await fetch(`${API_BASE}/api/admin/proposal/section-assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          proposal_id: item.proposal_id,
          line_index: item.line_index,
          section_id: sectionId,
          section_name: sectionName,
          raw_text: item.suggested_section_name || item.description || null,
        }),
      });
      if (!r1.ok) {
        const errBody = await r1.json().catch(() => ({}));
        throw new Error(errBody.error || `section-assign: HTTP ${r1.status}`);
      }
      // (2) section_aliases_v2 に user_correct alias 追加 (= 辞書学習)
      const rawText = item.suggested_section_name || item.description || null;
      if (rawText) {
        const r2 = await fetch(`${API_BASE}/api/admin/section-aliases/add`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            section_id: sectionId,
            alias: rawText,
            alias_type: 'user_correct',
            confidence: 1.0,
            source: 'user_correct',
          }),
        });
        // alias add は best-effort (= 既存 alias は skip、 失敗しても section 確定は完了済)
        if (!r2.ok) {
          console.warn('[section-aliases/add] non-fatal failure:', r2.status);
        }
      }
      setSavedKey(key);
      setTimeout(() => loadAll(), 600);
    } catch (e: any) {
      setSaveError(e.message || 'save failed');
    } finally {
      setSavingKey(null);
    }
  }, [selected, sections, loadAll]);

  return (
    <div style={STYLES.page}>
      <Container>
        <Breadcrumbs
          links={[
            { url: '#', title: 'FREEEMAN' },
            { url: '#section_unknown', title: '部門不明一覧' },
          ]}
        />
        <div style={{ marginTop: '16px', marginBottom: '12px' }}>
          <HStack gap={2} alignItems="center">
            <MdHelp size={28} color="#d97706" />
            <PageTitle>部門不明一覧</PageTitle>
          </HStack>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <Note>
            G-step3d (2026-05-21): proposal details に section_id 未確定 line を 全件 list 化。
            18 freee 部門 から選択して Save で proposed_deal_json 確定 + section_aliases_v2 に
            user_correct alias として 学習。 次回以降 同じ PDF 文言は 機械逆引きで 確定する。
          </Note>
        </div>

        <CardBase>
          <div style={{ padding: '16px' }}>
            <VStack gap={2}>
              <HStack gap={2} alignItems="center" justifyContent="space-between">
                <Text weight="bold">
                  {loading ? '読込中...' : `${items.length} 件 (= section_id 未確定 line)`}
                </Text>
                <Button onClick={loadAll} disabled={loading}>
                  <MdRefresh /> 再読込
                </Button>
              </HStack>

              {error && <MessageBlock success={false}>{error}</MessageBlock>}
              {saveError && <MessageBlock success={false}>{saveError}</MessageBlock>}

              {loading && (
                <div style={{ padding: '24px' }}>
                  <HStack gap={1} alignItems="center">
                    <InlineSpinner isLoading={true} /> <Text>読込中...</Text>
                  </HStack>
                </div>
              )}

              {!loading && items.length === 0 && (
                <div style={{ padding: '32px', textAlign: 'center' }}>
                  <NoDataIllust />
                  <div style={{ marginTop: '12px' }}>
                    <Text>部門不明 line はありません</Text>
                  </div>
                </div>
              )}

              {!loading && items.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={STYLES.table}>
                    <thead>
                      <tr>
                        <th style={STYLES.thCell}>RID</th>
                        <th style={STYLES.thCell}>ref_number</th>
                        <th style={STYLES.thCell}>line#</th>
                        <th style={STYLES.thCell}>摘要</th>
                        <th style={STYLES.thCell}>金額</th>
                        <th style={STYLES.thCell}>PDF 部門 raw</th>
                        <th style={STYLES.thCell}>確定する部門</th>
                        <th style={STYLES.thCell}>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it) => {
                        const key = `${it.proposal_id}:${it.line_index}`;
                        const isSaving = savingKey === key;
                        const isSaved = savedKey === key;
                        return (
                          <tr key={key}>
                            <td style={STYLES.tdCell}>
                              <code>{it.arica_rowid || '—'}</code>
                              {it.partner_name && (
                                <div style={{ fontSize: '0.6875rem', color: '#888' }}>{it.partner_name}</div>
                              )}
                            </td>
                            <td style={STYLES.tdCell}>
                              <Text size={0.75}>{it.ref_number}</Text>
                            </td>
                            <td style={STYLES.tdCell}>{it.line_index}</td>
                            <td style={STYLES.tdCell} title={it.description}>
                              <span style={{ fontSize: '0.75rem' }}>
                                {(it.description || '').slice(0, 40)}
                                {(it.description || '').length > 40 ? '…' : ''}
                              </span>
                            </td>
                            <td style={STYLES.tdCell}>{fmtAmount(it.amount)}</td>
                            <td style={STYLES.tdCell}>
                              {it.suggested_section_name ? (
                                <span style={STYLES.badge('#d97706')}>{it.suggested_section_name}</span>
                              ) : (
                                <span style={{ color: '#c4c0bc' }}>—</span>
                              )}
                            </td>
                            <td style={STYLES.tdCell}>
                              <select
                                style={STYLES.select}
                                value={selected[key] || ''}
                                onChange={(e) =>
                                  setSelected({ ...selected, [key]: Number(e.target.value) })
                                }
                                disabled={isSaving || isSaved}
                              >
                                <option value="">-- 選択 --</option>
                                {sections.map((s) => (
                                  <option key={s.section_id} value={s.section_id}>
                                    {s.long_name || s.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td style={STYLES.tdCell}>
                              {isSaved ? (
                                <span style={STYLES.badge('#10b981')}>確定済</span>
                              ) : (
                                <Button
                                  appearance="primary"
                                  onClick={() => doSave(it)}
                                  disabled={isSaving || !selected[key]}
                                >
                                  {isSaving ? <InlineSpinner isLoading={true} /> : <MdSave />} 確定
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </VStack>
          </div>
        </CardBase>
      </Container>
    </div>
  );
};

export default SectionUnknownPage;
